import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientSidebar from "../components/portal/ClientSidebar";
import ClientTopBar from "../components/portal/ClientTopBar";
import {
  useClientProjectsStore,
  type ClientProjectRow,
} from "../store/useClientProjectsStore";
import {
  MIcon,
  StatusChip,
  fmtDate,
  money,
  stageIcon,
  EmptyState,
} from "../components/portal/projects/shared";
import AiProjectSummaryModal from "../components/portal/projects/AiProjectSummaryModal";
import ProjectDetailsModal from "../components/portal/projects/ProjectDetailsModal";
import ProjectLifecycleModal from "../components/portal/projects/ProjectLifecycleModal";
import NewProjectRequestModal from "../components/portal/projects/NewProjectRequestModal";
import MyRequestsPanel from "../components/portal/projects/MyRequestsPanel";

/**
 * The client's Projects workspace.
 *
 * Reads as: quantitative snapshot → what it means → the full list → one project
 * in detail. Nothing on this page is computed in the browser; the API decides
 * Status, Current Stage, Health, progress and value so every surface agrees.
 */

// ── Card 1: Active vs Closed ─────────────────────────────────────────────────
const ActiveVsClosed = ({
  total,
  active,
  closed,
  onPick,
}: {
  total: number;
  active: number;
  closed: number;
  onPick: (quick: "active" | "closed") => void;
}) => {
  const pct = total ? Math.round((active / total) * 100) : 0;
  return (
    <div className="bg-surface-container-lowest p-lg rounded-xl border border-outline-variant shadow-sm flex flex-col justify-between">
      <div className="flex justify-between items-center mb-md">
        <span className="text-[11px] font-bold text-tertiary uppercase tracking-wider">
          Active vs Closed Projects
        </span>
      </div>
      <div className="flex items-center py-sm gap-md flex-row">
        <div className="relative w-32 h-32 flex items-center justify-center flex-shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" stroke="#d3e4fe" strokeWidth="4" />
            <circle
              cx="18"
              cy="18"
              r="14"
              fill="none"
              stroke="rgb(var(--brand-primary))"
              strokeWidth="4"
              strokeDasharray={`${pct}, 100`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute flex flex-col items-center justify-center text-center">
            <span className="text-display-md font-bold text-on-surface leading-none">{total}</span>
            <span className="text-[10px] uppercase font-bold text-tertiary tracking-wider mt-0.5">
              Total
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2 flex-1 w-full justify-center">
          <button
            onClick={() => onPick("active")}
            className="flex items-center justify-between p-2 rounded-lg bg-surface-container-low/60 hover:bg-surface-container-low transition-colors border border-transparent hover:border-primary/30 group text-left"
          >
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-primary flex-shrink-0" />
              <span className="text-xs font-semibold text-on-surface group-hover:text-primary transition-colors">
                Active
              </span>
            </span>
            <span className="flex items-center gap-1">
              <span className="text-sm font-bold text-primary">{active}</span>
              <span className="text-[11px] text-on-surface-variant font-medium">({pct}%)</span>
            </span>
          </button>
          <button
            onClick={() => onPick("closed")}
            className="flex items-center justify-between p-2 rounded-lg bg-surface-container-low/60 hover:bg-surface-container-low transition-colors border border-transparent hover:border-outline-variant group text-left"
          >
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-outline-variant flex-shrink-0" />
              <span className="text-xs font-semibold text-on-surface-variant">Closed</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="text-sm font-bold text-on-surface-variant">{closed}</span>
              <span className="text-[11px] text-on-surface-variant font-medium">
                ({total ? 100 - pct : 0}%)
              </span>
            </span>
          </button>
        </div>
      </div>
      <p className="text-[11px] text-on-surface-variant mt-1">
        {total} {total === 1 ? "Project" : "Projects"} · {active} Active · {closed} Closed
      </p>
    </div>
  );
};

// ── Card 2: Awaiting Actions ─────────────────────────────────────────────────
// This is not a score. It only says: these are the things waiting for you.
const AwaitingActions = ({
  total,
  buckets,
  onPick,
}: {
  total: number;
  buckets: Record<string, number>;
  onPick: (type: string) => void;
}) => {
  const rows = [
    { label: "Review", type: "Review" },
    { label: "Feedback", type: "Provide Feedback" },
    { label: "Approval", type: "Approve" },
    { label: "Information Required", type: "Provide Information" },
    { label: "Invoice / Payment", type: "Make Payment" },
  ];
  const max = Math.max(1, ...rows.map((r) => buckets[r.label] || 0));

  return (
    <div className="bg-surface-container-lowest p-lg rounded-xl border border-outline-variant shadow-sm flex flex-col">
      <div className="flex justify-between items-center mb-md">
        <span className="text-[11px] font-bold text-tertiary uppercase tracking-wider">
          Awaiting Actions
        </span>
        <span className="text-[11px] font-bold text-on-surface-variant">
          {total} Total {total === 1 ? "Action" : "Actions"}
        </span>
      </div>

      {total === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-md">
          <div className="w-10 h-10 rounded-xl bg-tertiary-container text-tertiary grid place-items-center mb-2">
            <MIcon name="task_alt" className="text-[20px]" />
          </div>
          <p className="text-body-sm font-semibold text-on-surface">Nothing needs you right now</p>
          <p className="text-[11px] text-on-surface-variant mt-0.5">
            We will let you know the moment something does.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 flex-1 justify-center">
          {rows.map((r) => {
            const n = buckets[r.label] || 0;
            return (
              <button
                key={r.label}
                onClick={() => n && onPick(r.type)}
                disabled={!n}
                className="flex items-center gap-2 text-left group disabled:cursor-default"
              >
                <span className="text-[11px] font-semibold text-on-surface-variant w-[120px] flex-shrink-0 group-enabled:group-hover:text-primary transition-colors">
                  {r.label}
                </span>
                <span className="flex-1 h-2.5 bg-surface-container rounded-full overflow-hidden">
                  <span
                    className="block h-full bg-primary rounded-full transition-all"
                    style={{ width: `${(n / max) * 100}%` }}
                  />
                </span>
                <span className="text-xs font-bold text-on-surface w-4 text-right">{n}</span>
              </button>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-on-surface-variant mt-md">
        {total ? `${total} ${total === 1 ? "action" : "actions"} awaiting your input` : "All clear"}
      </p>
    </div>
  );
};

// ── Card 3: Project Value & Volume ───────────────────────────────────────────
const ValueAndVolume = ({ v }: { v: any }) => {
  const activePct = v.totalValue ? Math.round((v.activeValue / v.totalValue) * 100) : 0;
  return (
    <div className="bg-surface-container-lowest p-lg rounded-xl border border-outline-variant shadow-sm flex flex-col justify-between">
      <div className="flex justify-between items-center mb-md">
        <span className="text-[11px] font-bold text-tertiary uppercase tracking-wider">
          Project Value &amp; Volume
        </span>
        <span className="text-[11px] font-bold text-on-surface-variant">
          {v.totalProjects} Total {v.totalProjects === 1 ? "Project" : "Projects"}
        </span>
      </div>

      <div className="py-sm">
        <p className="text-display-md font-bold text-on-surface leading-none">
          {money(v.totalValue, v.currency)}
        </p>
        <p className="text-[11px] uppercase font-bold text-tertiary tracking-wider mt-1">
          Total Project Value
        </p>
      </div>

      <div className="mt-md">
        <div className="h-2.5 bg-surface-container rounded-full overflow-hidden flex">
          <span className="h-full bg-primary" style={{ width: `${activePct}%` }} />
          <span className="h-full bg-outline-variant flex-1" />
        </div>
        <div className="flex items-center justify-between mt-2 text-[11px]">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="font-semibold text-on-surface">Active Portfolio</span>
            <span className="font-bold text-on-surface">{money(v.activeValue, v.currency)}</span>
            <span className="text-on-surface-variant">· {v.activeProjects} Active</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-[11px]">
          <span className="w-2 h-2 rounded-full bg-outline-variant" />
          <span className="font-semibold text-on-surface-variant">Closed Portfolio</span>
          <span className="font-bold text-on-surface-variant">{money(v.closedValue, v.currency)}</span>
          <span className="text-on-surface-variant">· {v.closedProjects} Closed</span>
        </div>
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const ClientProjectsPage = () => {
  const navigate = useNavigate();
  const {
    overview,
    isLoadingOverview,
    projects,
    total,
    counts,
    options,
    isLoadingProjects,
    filters,
    setFilters,
    resetFilters,
    fetchOverview,
    fetchProjects,
  } = useClientProjectsStore();

  const [showFilters, setShowFilters] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [lifecycleId, setLifecycleId] = useState<string | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(filters.search);

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  // Typing filters as you go, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchDraft !== filters.search) setFilters({ search: searchDraft });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const activeFilterCount = useMemo(
    () =>
      [filters.category, filters.status, filters.stage, filters.health, filters.action, filters.from, filters.to].filter(
        Boolean
      ).length,
    [filters]
  );

  const pages = Math.max(1, Math.ceil(total / filters.limit));
  const start = total ? (filters.page - 1) * filters.limit + 1 : 0;
  const end = Math.min(total, filters.page * filters.limit);

  const quickTabs = [
    { key: "all", label: `All (${counts.all})` },
    { key: "active", label: `Active (${counts.active})` },
    { key: "awaiting", label: "Awaiting Action", badge: counts.awaiting },
    { key: "closed", label: `Closed (${counts.closed})` },
  ] as const;

  return (
    <div className="min-h-screen bg-background text-on-surface flex">
      <ClientSidebar active="projects" />
      <div className="flex-1 min-w-0">
        <ClientTopBar title="Projects" />

        <main className="p-4 lg:p-lg max-w-[1400px] mx-auto">
          {/* Page header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-md mb-xl">
            <div>
              <h1 className="text-display-md font-bold text-on-surface mb-xs tracking-tight">Projects</h1>
              <p className="text-body-md text-on-surface-variant max-w-2xl">
                View your active and completed projects, track progress, review milestones, and stay up
                to date with project updates and actions requiring your attention.
              </p>
            </div>
            <div className="flex items-center gap-sm flex-wrap flex-shrink-0">
              <button
                onClick={() => setRequestOpen(true)}
                title="Tell us about a project you would like to start"
                className="flex items-center gap-xs px-lg py-2 bg-primary text-on-primary rounded-lg text-body-sm font-semibold hover:bg-primary/90 transition-all shadow-sm active:opacity-90 whitespace-nowrap"
              >
                <MIcon name="add" className="text-[18px]" />
                Start New Project
              </button>
            </div>
          </div>

          {/* Project Overview & AI Insights */}
          <div className="mb-xl">
            <div className="flex items-center gap-2 mb-md">
              <MIcon name="auto_awesome" className="text-primary text-[20px]" />
              <h2 className="text-[12px] font-bold uppercase tracking-wider text-on-surface">
                PROJECT OVERVIEW &amp; AI INSIGHTS
              </h2>
              <span className="px-2 py-0.5 bg-primary/10 text-primary text-[10px] font-bold rounded-full uppercase tracking-wider">
                AI Powered
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg mb-md">
              {isLoadingOverview && !overview ? (
                [0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="bg-surface-container-lowest p-lg rounded-xl border border-outline-variant shadow-sm h-[248px] animate-pulse"
                  />
                ))
              ) : overview ? (
                <>
                  <ActiveVsClosed
                    total={overview.activeVsClosed.total}
                    active={overview.activeVsClosed.active}
                    closed={overview.activeVsClosed.closed}
                    onPick={(quick) => setFilters({ quick })}
                  />
                  <AwaitingActions
                    total={overview.awaitingActions.total}
                    buckets={overview.awaitingActions.buckets}
                    onPick={(action) => setFilters({ action, quick: "all" })}
                  />
                  <ValueAndVolume v={overview.valueAndVolume} />
                </>
              ) : null}
            </div>

            {/* Short AI insight.
                Its tone follows the data, not the prose: an insight about a
                confirmed problem is presented as one, a risk is presented as a
                risk, and everything else stays neutral. The counts come from
                the API alongside the sentence, so the UI never has to infer
                severity by reading the text. */}
            {overview && (() => {
              const att = overview.attention;
              const confirmed =
                (att?.confirmedIssues || 0) + (att?.invoicesBlockingWork || 0) + (att?.overdueActions || 0);
              const potential = att?.potentialRisks || 0;
              const tone = confirmed
                ? "bg-error-container/30 border-error/30"
                : potential
                ? "bg-amber-50/70 border-amber-300/50"
                : "bg-surface-container-low border-outline-variant/40";
              const icon = confirmed ? "error" : potential ? "warning" : "insights";
              const iconTone = confirmed
                ? "bg-error/10 text-error"
                : potential
                ? "bg-amber-100 text-amber-700"
                : "bg-primary/10 text-primary";
              return (
              <div className={`p-md rounded-xl border shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-md ${tone}`}>
                <div className="flex items-start sm:items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconTone}`}>
                    <MIcon name={icon} className="text-[18px]" />
                  </div>
                  <p className="text-body-sm text-on-surface font-medium leading-relaxed">
                    {overview.insight}
                  </p>
                </div>
                <button
                  onClick={() => setAiOpen(true)}
                  className="inline-flex items-center gap-1 text-body-sm font-bold text-primary hover:underline whitespace-nowrap flex-shrink-0"
                >
                  View More
                  <MIcon name="arrow_forward" className="text-[16px]" />
                </button>
              </div>
              );
            })()}
          </div>

          <MyRequestsPanel onOpenProject={(id) => setDetailsId(id)} />

          {/* All Projects */}
          <div className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden shadow-sm">
            <div className="p-md border-b border-outline-variant flex flex-col sm:flex-row sm:items-center justify-between gap-md">
              <div className="flex items-center gap-xs overflow-x-auto">
                {quickTabs.map((t) => {
                  const on = filters.quick === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setFilters({ quick: t.key })}
                      className={`px-md py-1.5 rounded-lg text-body-sm transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                        on
                          ? "bg-primary text-on-primary font-bold shadow-sm"
                          : "text-on-surface-variant font-semibold hover:bg-surface-container-low"
                      }`}
                    >
                      {t.label}
                      {"badge" in t && t.badge ? (
                        <span
                          className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                            on ? "bg-on-primary/20 text-on-primary" : "bg-error-container text-error"
                          }`}
                        >
                          {t.badge}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-sm">
                <div className="relative min-w-[260px]">
                  <MIcon
                    name="search"
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-outline text-[16px]"
                  />
                  <input
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    type="text"
                    placeholder="Search projects by name, category, milestone..."
                    className="w-full pl-8 pr-3 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-body-sm text-on-surface focus:ring-0 focus:border-primary outline-none transition-all"
                  />
                </div>
                <button
                  onClick={() => setShowFilters((v) => !v)}
                  title="Advanced Filters"
                  className={`flex items-center gap-1 px-md py-1.5 border rounded-lg text-body-sm font-semibold transition-colors shadow-sm ${
                    activeFilterCount
                      ? "border-primary text-primary bg-primary/5"
                      : "border-outline-variant text-on-surface bg-surface-container-lowest hover:bg-surface-container-low"
                  }`}
                >
                  <MIcon name="tune" className="text-[16px]" />
                  {activeFilterCount ? activeFilterCount : ""}
                </button>
              </div>
            </div>

            {/* Advanced filters */}
            {showFilters && (
              <div className="p-md border-b border-outline-variant bg-surface-container-low/50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-sm">
                {[
                  { key: "category", label: "Category", values: options.categories },
                  { key: "status", label: "Status", values: options.statuses },
                  { key: "stage", label: "Current Stage", values: options.stages },
                  { key: "health", label: "Project Health", values: options.healths },
                  { key: "action", label: "Client Action", values: options.actions },
                ].map((f) => (
                  <label key={f.key} className="block">
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-tertiary mb-1">
                      {f.label}
                    </span>
                    <select
                      value={(filters as any)[f.key]}
                      onChange={(e) => setFilters({ [f.key]: e.target.value } as any)}
                      className="w-full px-2.5 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-body-sm outline-none focus:border-primary"
                    >
                      <option value="">All</option>
                      {f.values.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
                <label className="block">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-tertiary mb-1">
                    ETA From
                  </span>
                  <input
                    type="date"
                    value={filters.from}
                    onChange={(e) => setFilters({ from: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-body-sm outline-none focus:border-primary"
                  />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-tertiary mb-1">
                    ETA To
                  </span>
                  <input
                    type="date"
                    value={filters.to}
                    onChange={(e) => setFilters({ to: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-body-sm outline-none focus:border-primary"
                  />
                </label>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      resetFilters();
                      setSearchDraft("");
                    }}
                    className="px-md py-1.5 rounded-lg border border-outline-variant text-body-sm font-semibold text-on-surface-variant hover:bg-surface-container-low"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-surface-container-low">
                  <tr>
                    {["Project", "Category", "Status", "Milestone", "ETA"].map((h) => (
                      <th key={h} className="px-lg py-md text-label-md text-tertiary uppercase">
                        {h}
                      </th>
                    ))}
                    <th className="px-lg py-md text-label-md text-tertiary uppercase min-w-[140px]">
                      Progress
                    </th>
                    <th className="px-lg py-md text-label-md text-tertiary uppercase">Current Stage</th>
                    <th className="px-lg py-md text-label-md text-tertiary uppercase text-right">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {isLoadingProjects && !projects.length ? (
                    <tr>
                      <td colSpan={8} className="px-lg py-xl text-center text-body-sm text-on-surface-variant">
                        Loading your projects…
                      </td>
                    </tr>
                  ) : !projects.length ? (
                    <tr>
                      <td colSpan={8}>
                        <EmptyState
                          icon="work"
                          title={
                            counts.all === 0
                              ? "You do not have any projects yet"
                              : "No projects match these filters"
                          }
                          detail={
                            counts.all === 0
                              ? "Start a new project request and it will appear here once it has been set up."
                              : "Try clearing a filter or searching for something else."
                          }
                        />
                      </td>
                    </tr>
                  ) : (
                    projects.map((p: ClientProjectRow) => (
                      <tr key={p.id} className="hover:bg-surface-container-low transition-colors">
                        <td className="px-lg py-md">
                          <div className="text-body-md font-semibold text-on-surface">{p.name}</div>
                          {p.reference && (
                            <div className="text-xs text-primary font-medium">#{p.reference}</div>
                          )}
                        </td>
                        <td className="px-lg py-md text-body-md text-on-surface-variant">
                          {p.category || "—"}
                        </td>
                        <td className="px-lg py-md">
                          <StatusChip status={p.status} />
                        </td>
                        <td className="px-lg py-md text-body-md text-on-surface font-medium">
                          {p.milestone || "—"}
                        </td>
                        <td className="px-lg py-md text-body-md text-on-surface-variant whitespace-nowrap">
                          {fmtDate(p.eta)}
                        </td>
                        <td className="px-lg py-md">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-surface-container rounded-full overflow-hidden min-w-[60px]">
                              <div
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${p.progress}%` }}
                              />
                            </div>
                            <span className="text-body-sm font-bold text-on-surface">{p.progress}%</span>
                          </div>
                        </td>
                        <td className="px-lg py-md">
                          <div className="inline-flex items-center gap-1.5 text-body-sm text-secondary font-semibold">
                            <MIcon name={stageIcon(p.currentStage)} className="text-[16px]" />
                            {p.currentStage || "—"}
                          </div>
                          <div className="mt-0.5">
                            <button
                              onClick={() => setLifecycleId(p.id)}
                              className="text-[11px] text-primary hover:underline font-medium inline-flex items-center gap-0.5"
                            >
                              View Lifecycle
                              <MIcon name="arrow_forward" className="text-[12px]" />
                            </button>
                          </div>
                        </td>
                        <td className="px-lg py-md text-right">
                          <button
                            onClick={() => setDetailsId(p.id)}
                            className="px-md py-1 rounded bg-surface-container-low hover:bg-primary hover:text-on-primary text-primary text-label-md font-bold transition-all shadow-sm whitespace-nowrap"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="p-md border-t border-outline-variant flex flex-col sm:flex-row sm:items-center justify-between gap-sm">
                <p className="text-body-sm text-on-surface-variant">
                  Showing <b className="text-on-surface">{start}</b>–<b className="text-on-surface">{end}</b>{" "}
                  of <b className="text-on-surface">{total}</b> projects
                </p>
                <div className="flex items-center gap-sm">
                  <label className="flex items-center gap-1.5 text-body-sm text-on-surface-variant">
                    Rows per page:
                    <select
                      value={filters.limit}
                      onChange={(e) => setFilters({ limit: Number(e.target.value) })}
                      className="px-2 py-1 bg-surface-container-low border border-outline-variant rounded-lg text-body-sm outline-none focus:border-primary"
                    >
                      {[10, 25, 50].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="flex items-center gap-1">
                    <button
                      disabled={filters.page <= 1}
                      onClick={() => setFilters({ page: filters.page - 1 })}
                      className="p-1.5 rounded-lg border border-outline-variant text-on-surface-variant disabled:opacity-40 hover:bg-surface-container-low"
                      aria-label="Previous page"
                    >
                      <MIcon name="chevron_left" className="text-[18px]" />
                    </button>
                    <span className="text-body-sm font-semibold text-on-surface px-1">
                      {filters.page} / {pages}
                    </span>
                    <button
                      disabled={filters.page >= pages}
                      onClick={() => setFilters({ page: filters.page + 1 })}
                      className="p-1.5 rounded-lg border border-outline-variant text-on-surface-variant disabled:opacity-40 hover:bg-surface-container-low"
                      aria-label="Next page"
                    >
                      <MIcon name="chevron_right" className="text-[18px]" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <NewProjectRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} />

      <AiProjectSummaryModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onOpenProject={(id) => {
          setAiOpen(false);
          setDetailsId(id);
        }}
      />
      <ProjectDetailsModal
        projectId={detailsId}
        onClose={() => setDetailsId(null)}
        onViewLifecycle={(id) => {
          setDetailsId(null);
          setLifecycleId(id);
        }}
      />
      <ProjectLifecycleModal projectId={lifecycleId} onClose={() => setLifecycleId(null)} />
    </div>
  );
};

export default ClientProjectsPage;
